import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import axios from 'axios'; // Import axios
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Grid, TextField, Card, CardContent, Typography, Button, IconButton, LinearProgress } from '@mui/material';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { styled, css } from '@mui/system';
import { Modal as BaseModal } from '@mui/base/Modal';
import CloseIcon from '@mui/icons-material/Close';


// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CACHE_DURATION = 60 * 60 * 1000; // Cache duration in milliseconds (1 hour)
const RATE_LIMIT_DELAY = 2000; // 2 seconds delay to stay within 30 requests per minute

const fetchWithCache = async (url) => {
  const cacheKey = `cache_${url}`;
  const cachedData = localStorage.getItem(cacheKey);
  const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);

  if (cachedData && cacheTimestamp && new Date().getTime() - cacheTimestamp < CACHE_DURATION) {
    return JSON.parse(cachedData);
  }

  const data = await fetchData(url);
  localStorage.setItem(cacheKey, JSON.stringify(data));
  localStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime());
  return data;
};

const fetchWithRateLimit = (() => {
  let queue = [];
  let isProcessing = false;

  const processQueue = async () => {
    if (isProcessing || queue.length === 0) return;
    isProcessing = true;

    while (queue.length > 0) {
      const { url, resolve, reject } = queue.shift();
      try {
        const response = await axios.get(url);
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
      await new Promise((r) => setTimeout(r, RATE_LIMIT_DELAY)); // Delay between requests
    }
    isProcessing = false;
  };

  return (url) =>
    new Promise((resolve, reject) => {
      queue.push({ url, resolve, reject });
      processQueue();
    });
})();

const fetchData = async (url) => {
  try {
    const data = await fetchWithRateLimit(url);
    console.log("Data fetched:", data);
    return data;
  } catch (error) {
    console.error("Fetch failed:", error.message);
    throw error;
  }
};

const formatPrice = (price) => {
  if (price === undefined || price === null) return 'N/A';
  if (price < 1) return price.toString(); // Return as is if less than 1
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const WhatIf = ({ open, onClose, portfolio }) => {
  const [token, setToken] = useState('');
  const [amount, setAmount] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [futurePrice, setFuturePrice] = useState('');
  const [totalSupply, setTotalSupply] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [investment, setInvestment] = useState(null);
  const [futureValue, setFutureValue] = useState(null);
  const [profit, setProfit] = useState(null);
  const [profitPercentage, setProfitPercentage] = useState(null);
  const [futureMarketCap, setFutureMarketCap] = useState(null);
  const [feasibility, setFeasibility] = useState('');
  const [btcMarketCap, setBtcMarketCap] = useState(null);
  const [comparisonToken, setComparisonToken] = useState('');
  const [comparisonTokenData, setComparisonTokenData] = useState(null);
  const [comparisonSearchResults, setComparisonSearchResults] = useState([]);
  const [calculated, setCalculated] = useState(false); // Add state to track if calculation is done

  const fetchSearchResults = useCallback(
    debounce(async (query) => {
      if (!query.trim()) return setSearchResults([]);

      setLoading(true);
      try {
        const data = await fetchWithCache(`https://api.coingecko.com/api/v3/search?query=${query}`);
        if (data && data.coins) {
          setSearchResults(data.coins.slice(0, 5)); // Limit results to top 5
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        setError('Error fetching search results. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 300), // Reduce debounce delay to 300ms
    []
  );

  useEffect(() => {
    if (token) {
      fetchSearchResults(token);
    } else {
      setSearchResults([]);
    }
  }, [token, fetchSearchResults]);

  useEffect(() => {
    const fetchBtcMarketCap = async () => {
      try {
        const data = await fetchWithCache('https://api.coingecko.com/api/v3/coins/bitcoin');
        setBtcMarketCap(data.market_data.market_cap.usd);
        setComparisonTokenData(data);
      } catch (err) {
        setError('Failed to fetch Bitcoin market cap');
      }
    };
    fetchBtcMarketCap();
  }, []);

  const handleSelect = async (result) => {
    setLoading(true);
    try {
      const data = await fetchWithCache(`https://api.coingecko.com/api/v3/coins/${result.id}`);
      setSelectedToken({
        id: result.id,
        name: result.name,
        symbol: result.symbol,
        thumb: result.thumb || '',
        total_supply: data.market_data.total_supply || '',
        current_price: data.market_data.current_price.usd || 'N/A', // Add current price
        market_cap: data.market_data.market_cap.usd || 'N/A', // Add market cap
      });
      setTotalSupply(data.market_data.total_supply || ''); // Set total supply
      setToken('');
      setSearchResults([]);
    } catch (err) {
      setError('Failed to fetch token details');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveToken = () => {
    setSelectedToken(null);
    setTotalSupply(''); // Clear total supply when token is removed
  };

  const handleSelectComparisonToken = async (result) => {
    setLoading(true);
    try {
      const data = await fetchWithCache(`https://api.coingecko.com/api/v3/coins/${result.id}`);
      setComparisonToken(result.id);
      setComparisonTokenData(data);
      setComparisonSearchResults([]); // Clear search results after selection
      localStorage.removeItem(`search_${comparisonToken}`); // Remove unpicked tokens from local storage
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveComparisonToken = () => {
    setComparisonToken('');
    setComparisonTokenData(null);
  };

  const fetchComparisonSearchResults = debounce(async (query) => {
    if (query) {
      setLoading(true);
      try {
        const data = await fetchWithCache(`https://api.coingecko.com/api/v3/search?query=${query}`);
        setComparisonSearchResults(data.coins.slice(0, 10));
        localStorage.setItem(`search_${query}`, JSON.stringify(data.coins.slice(0, 10)));
      } catch (err) {
        setError(err.message);
        setComparisonSearchResults([]);
      } finally {
        setLoading(false);
      }
    } else {
      setComparisonSearchResults([]);
    }
  }, 500);

  useEffect(() => {
    if (comparisonToken) {
      const cachedResults = localStorage.getItem(`search_${comparisonToken}`);
      if (cachedResults) {
        setComparisonSearchResults(JSON.parse(cachedResults));
      } else {
        fetchComparisonSearchResults(comparisonToken);
      }
    } else {
      setComparisonSearchResults([]);
    }
  }, [comparisonToken]);

  const calculateScenario = () => {
    if (selectedToken && amount && purchasePrice && futurePrice && totalSupply) {
      const investmentAmount = parseFloat(amount) * parseFloat(purchasePrice);
      const futureValueAmount = parseFloat(amount) * parseFloat(futurePrice);
      const profitAmount = futureValueAmount - investmentAmount;
      const profitPercentageAmount = ((profitAmount / investmentAmount) * 100).toFixed(2);
      const futureMarketCapAmount = parseFloat(futurePrice) * parseFloat(totalSupply);
      const feasibilityText = futureMarketCapAmount > 1e12 ? 'Highly unlikely due to extremely high market cap requirements.' : 'Possible';

      setInvestment(investmentAmount.toFixed(2));
      setFutureValue(futureValueAmount.toFixed(2));
      setProfit(profitAmount.toFixed(2));
      setProfitPercentage(profitPercentageAmount);
      setFutureMarketCap(futureMarketCapAmount.toFixed(2));
      setFeasibility(feasibilityText);
      setCalculated(true); // Set calculated to true after calculation
    }
  };

  const resetForm = () => {
    setSelectedToken(null);
    setAmount('');
    setPurchasePrice('');
    setFuturePrice('');
    setTotalSupply('');
    setInvestment(null);
    setFutureValue(null);
    setProfit(null);
    setProfitPercentage(null);
    setFutureMarketCap(null);
    setFeasibility('');
    setCalculated(false); // Reset calculated state
  };

  const barData = {
    labels: ['Investment', 'Future Value'],
    datasets: [
      {
        label: 'Amount ($)',
        data: [investment, futureValue],
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
        barThickness: 20,
      },
    ],
  };

  const barOptions = {
    indexAxis: 'y', // Make the chart horizontal
    maintainAspectRatio: false, // Allow the chart to be more compressed
    scales: {
      x: {
        beginAtZero: true,
      },
      y: {
        beginAtZero: true,
        barThickness: 10, // Make bars thinner
      },
    },
  };

  const comparisonPriceAtMarketCap = comparisonTokenData && totalSupply 
  ? (comparisonTokenData.market_data.market_cap.usd / totalSupply).toFixed(6) 
  : null;

// Ensure comparisonPriceAtMarketCap is converted to a proper number
const numericComparisonPriceAtMarketCap = comparisonPriceAtMarketCap 
  ? parseFloat(comparisonPriceAtMarketCap) 
  : null;

// Ensure formattedComparisonPrice remains a valid number
const formattedComparisonPrice = numericComparisonPriceAtMarketCap && numericComparisonPriceAtMarketCap < 1 
  ? numericComparisonPriceAtMarketCap 
  : numericComparisonPriceAtMarketCap
    ? parseFloat(formatPrice(numericComparisonPriceAtMarketCap)) 
    : null;

const isValidNumber = (value) => !isNaN(value) && value !== null && value !== undefined;

const gainsX = isValidNumber(formattedComparisonPrice) && isValidNumber(selectedToken?.current_price)
  ? (formattedComparisonPrice / selectedToken.current_price).toFixed(2)
  : "N/A";


console.log("comparisonPriceAtMarketCap:", numericComparisonPriceAtMarketCap);
console.log("formattedComparisonPrice:", formattedComparisonPrice);
console.log("gainsX:", gainsX);


  return (
    <Modal
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={open}
      onClose={onClose}
      slots={{ backdrop: StyledBackdrop }}
    >
      <ModalContent sx={{ width: 900, fontFamily: 'var(--font-family)', WebkitFontSmoothing: 'var(--font-smoothing)' }}> {/* Make the modal wider */}
        <Box sx={{ display: open ? 'block' : 'none', padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h5" component="div">
                What If Scenario
              </Typography>
              {selectedToken ? (
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <img src={selectedToken.thumb || ''} alt={selectedToken.name} style={{ width: '20px', marginRight: '10px' }} /> {/* Add null check */}
                  <span>{selectedToken.name} ({selectedToken.symbol.toUpperCase()})</span>
                  <IconButton size="small" onClick={handleRemoveToken} style={{ marginLeft: '10px' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="Search Token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  helperText={error}
                  error={!!error}
                />
              )}
              {loading && <LinearProgress />}
              {searchResults.length > 0 && (
                <ul style={{ maxHeight: '200px', overflowY: 'scroll', listStyle: 'none', padding: 0 }}>
                  {searchResults.map((result) => (
                    <li
                      key={result.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #ddd',
                      }}
                      onMouseDown={() => handleSelect(result)} // Change onClick to onMouseDown
                    >
                      <img
                        src={result.thumb || 'https://via.placeholder.com/20'} // Use CoinGecko's `thumb` or a placeholder
                        alt={result.name}
                        style={{ width: '20px', height: '20px', marginRight: '10px' }}
                      />
                      <span>
                        <strong>{result.symbol.toUpperCase()}</strong> - {result.name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Number of Tokens"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Purchase Price (per token)"
                  type="number"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Future Price (per token)"
                  type="number"
                  value={futurePrice}
                  onChange={(e) => setFuturePrice(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Total Supply"
                  type="number"
                  value={totalSupply}
                  onChange={(e) => setTotalSupply(e.target.value)}
                  disabled={!!selectedToken} // Disable input if token is selected
                />
              </Box>
              <Typography variant="h6" component="div" sx={{ marginTop: 2 }}>
                Compare to:
              </Typography>
              {comparisonTokenData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <img src={comparisonTokenData.image.thumb || ''} alt={comparisonTokenData.name} style={{ width: '20px', marginRight: '10px' }} /> {/* Add null check */}
                  <span>{comparisonTokenData.name} ({comparisonTokenData.symbol.toUpperCase()})</span>
                  <IconButton size="small" onClick={handleRemoveComparisonToken} style={{ marginLeft: '10px' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="Search Comparison Token"
                  value={comparisonToken}
                  onChange={(e) => setComparisonToken(e.target.value)}
                  helperText={error}
                  error={!!error}
                />
              )}
              {loading && <LinearProgress />}
              {comparisonTokenData ? null : (
                comparisonSearchResults.length > 0 && (
                  <ul style={{ maxHeight: '200px', overflowY: 'scroll', listStyle: 'none', padding: 0 }}>
                    {comparisonSearchResults.map((result) => (
                      <li
                        key={result.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #ddd',
                        }}
                        onMouseDown={() => handleSelectComparisonToken(result)} // Change onClick to onMouseDown
                      >
                        <img
                          src={result.thumb || 'https://via.placeholder.com/20'} // Use CoinGecko's `thumb` or a placeholder
                          alt={result.name}
                          style={{ width: '20px', height: '20px', marginRight: '10px' }}
                        />
                        <span>
                          <strong>{result.symbol.toUpperCase()}</strong> - {result.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
              {investment && futureValue && profit ? (
                <Card sx={{ marginTop: 2 }}>
  <CardContent>
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>What if?</Typography>
    {selectedToken && (
      <>
        <Typography><b>Current Price:</b> ${formatPrice(selectedToken.current_price)}</Typography>
        <Typography><b>Current Market Cap:</b> ${formatPrice(selectedToken.market_cap)}</Typography>
        {formattedComparisonPrice && (
          <Typography>
            <b>
              <span style={{ color: 'blue' }}>{selectedToken.symbol.toUpperCase()}</span> Price at <span style={{ color: 'green' }}>{comparisonTokenData.name}</span> Market Cap:
            </b> ${formattedComparisonPrice}
          </Typography>
        )}
      </>
                    )}
                    <Typography><b>Gains X:</b> {gainsX}X</Typography> {/* Add Gains X field */}
    <hr />
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Scenario Results</Typography>
    <Typography><b>Total Investment:</b> ${formatPrice(investment)}</Typography>
    <Typography><b>Future Value:</b> ${formatPrice(futureValue)}</Typography>
    <Typography>
      <b>Profit:</b> 
      <span style={{ color: profit >= 0 ? 'green' : 'red' }}>
        ${formatPrice(profit)}
      </span>
    </Typography>
    <Typography>
      <b>Profit Percentage:</b> 
      <span style={{ color: profitPercentage >= 0 ? 'green' : 'red' }}>
        {profitPercentage}%
      </span>
    </Typography>
    <Typography><b>Future Market Cap:</b> ${formatPrice(futureMarketCap)}</Typography>
    <Typography><b>Feasibility:</b> {feasibility}</Typography>
    
    <Typography variant="h6" sx={{ marginTop: 2 }}>Visualization:</Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', height: '100px' }}> {/* Adjust height to make it more compressed */}
      <Bar data={barData} options={barOptions} />
    </Box>
  </CardContent>
</Card>

              ) : (
                <Card sx={{ marginTop: 2 }}>
                  <CardContent>
                    <Typography variant="h6" style={{ fontSize: '0.9em' }}>
                      <b>The "What If" Scenario Analysis Tool makes it easy for users to explore potential cryptocurrency investment outcomes.</b>
                    </Typography>
                    <Typography style={{ fontSize: '0.9em' }}>
                      • <b>Simulate Investments:</b> Enter token, quantity, purchase, and future price.
                    </Typography>
                    <Typography style={{ fontSize: '0.9em' }}>
                      • <b>Calculate Returns:</b> See total investment, profit, and percentage.
                    </Typography>
                    <Typography style={{ fontSize: '0.9em' }}>
                      • <b>Compare Market Caps:</b> Match your token's market cap to Bitcoin or others.
                    </Typography>
                    <Typography style={{ fontSize: '0.9em' }}>
                      • <b>Feasibility Check:</b> Assess if the target price is realistic.
                      </Typography>
                      <br />
                    <Typography style={{ fontSize: '0.9em' }}>
                      This tool empowers you to evaluate investment scenarios and understand the relationship between price, market cap, and feasibility.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
            <button className="button button-purple" onClick={calculated ? resetForm : calculateScenario}>
              {calculated ? 'Try Again' : 'Calculate'}
            </button>
            <Button onClick={onClose}>Close</Button>
          </Box>
        </Box>
      </ModalContent>
    </Modal>
  );
};

WhatIf.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const Backdrop = React.forwardRef((props, ref) => {
  const { open, className, ...other } = props;
  return (
    <div
      className={clsx({ 'base-Backdrop-open': open }, className)}
      ref={ref}
      {...other}
    />
  );
});

Backdrop.propTypes = {
  className: PropTypes.string.isRequired,
  open: PropTypes.bool,
};

const blue = {
  200: '#99CCFF',
  300: '#66B2FF',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  700: '#0066CC',
};

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled('div')(
  ({ theme }) => css`
    font-family: var(--font-family);
    font-weight: 500;
    text-align: start;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
    border-radius: 8px;
    border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    box-shadow: 0 4px 12px
      ${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
    padding: 24px;
    color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};

    & .modal-title {
      margin: 0;
      line-height: 1.5rem;
      margin-bottom: 8px;
    }

    & .modal-description {
      margin: 0;
      line-height: 1.5rem;
      font-weight: 400;
      color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
      margin-bottom: 4px;
    }
  `,
);

export default WhatIf;
